import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Grid,
  Text,
  Spinner,
  Select,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tooltip as ChakraTooltip,
} from '@chakra-ui/react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import {
  Hash,
  FileText,
  CurrencyDollar,
  Percent,
  WarningCircle,
  ArrowSquareOut,
} from 'phosphor-react';
import { useAuditoriaInteligente, IPeriodoParams } from '../../../../hooks/useAuditoriaInteligente';
import { formatCurrency } from '../../../../utils/FormatFunctions';
import { num, pct, qtd } from '../format';
import { IAuditoriaEmpresa, IImpostosPorNota, IImpNotasPage, NotaTipo } from '../types';
import { KpiCard } from '../components/KpiCard';
import { InfoBloco, FILTRO } from '../components/InfoPopover';
import { impostoDesc } from './_shared/impostos';
import { EmptyState } from '../components/EmptyState';
import { NoteDrawer } from './NoteDrawer';
import { UI } from '../ui';

interface ImpostosPorNotaScreenProps {
  empresa: IAuditoriaEmpresa | null;
  periodo: IPeriodoParams;
  /** Drill: abre Notas Fiscais filtrado pelo parceiro clicado (aba Por Cliente). */
  onOpenNotas: (busca: string, tipo?: 'saida' | 'entrada' | 'servico') => void;
  /** Cruzamento: abre os Impostos Apurados (fonte confiável enquanto o ETL não corrige). */
  onScreen: (screen: string) => void;
}

type Aba = 'nota' | 'imposto' | 'cfop' | 'uf' | 'cliente';

const TIPOS: { value: NotaTipo; label: string }[] = [
  { value: 'saida', label: 'Saída' },
  { value: 'entrada', label: 'Entrada' },
  { value: 'servico', label: 'Serviço' },
];

const ABAS: { value: Aba; label: string }[] = [
  { value: 'nota', label: 'Por Nota' },
  { value: 'imposto', label: 'Por Imposto' },
  { value: 'cfop', label: 'Por CFOP' },
  { value: 'uf', label: 'Por UF' },
  { value: 'cliente', label: 'Por Cliente' },
];

/** Cores das séries de impostos no gráfico de evolução. */
const SERIE_COLORS = [UI.brand, UI.pos, UI.warn, '#8B5CF6', '#1AA6B7', UI.neg, '#EC6C9C'];

/** mes vem como 'YYYY-MM-DD' (DATE_TRUNC month) → rótulo 'MM/AA'. */
const mesLabel = (m: string): string => {
  const [y, mo] = (m ?? '').split('-');
  return mo && y ? `${mo}/${y.slice(2)}` : m;
};

/** "UF · CNPJ" com fallback — evita renderizar " · " vazio quando faltam (comum em
 *  notas a pessoa fisica / serviço, onde UF/CNPJ nao vem do cadastro). */
const ufCnpj = (uf?: string | null, cnpj?: string | null) =>
  [uf, cnpj].filter(Boolean).join(' · ') || '—';

function Empty({ colSpan }: { colSpan: number }) {
  return (
    <Tr>
      <Td colSpan={colSpan} textAlign="center" py={6} color={UI.muted}>
        Sem dados no período.
      </Td>
    </Tr>
  );
}

function Painel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box
      bg={UI.glass}
      border="1px solid"
      borderColor={UI.glassBorder}
      borderRadius="16px"
      backdropFilter={UI.glassBlur}
      boxShadow={UI.glassShadow}
      p={5}
    >
      <Text fontSize="13px" fontWeight={700} mb={4} color={UI.ink}>
        {title}
      </Text>
      {children}
    </Box>
  );
}

/** Célula CFOP: mostra o NÚMERO e, no tooltip, o que ele significa (natureza). Quando o
 *  número não veio na extração, mostra a natureza (truncada pela Td) com tooltip. */
function CfopCelula({ cfop, natureza }: { cfop: string | null; natureza: string | null }) {
  if (cfop && natureza) {
    return (
      <ChakraTooltip label={natureza} hasArrow openDelay={300}>
        <Text as="span" cursor="help" borderBottom="1px dashed" borderColor={UI.border}>
          {cfop}
        </Text>
      </ChakraTooltip>
    );
  }
  if (cfop) {
    return <Text as="span">{cfop}</Text>;
  }
  if (natureza) {
    return (
      <ChakraTooltip label={natureza} hasArrow openDelay={300}>
        <Text as="span" fontSize="11px" color={UI.muted} cursor="help">
          {natureza}
        </Text>
      </ChakraTooltip>
    );
  }
  return (
    <Text as="span" color={UI.muted}>
      —
    </Text>
  );
}

export function ImpostosPorNotaScreen({
  empresa,
  periodo,
  onOpenNotas,
  onScreen,
}: ImpostosPorNotaScreenProps) {
  const { getImpostosPorNota, getImpostosPorNotaNotas } = useAuditoriaInteligente();
  const [tipo, setTipo] = useState<NotaTipo>('saida');
  // '' = sem filtro. Enviar string vazia ao backend casaria CAST(modelo)='' → nada;
  // por isso o queryKey usa `|| undefined` para omitir o parâmetro quando "Todos".
  const [imposto, setImposto] = useState('');
  const [modelo, setModelo] = useState('');
  const [cfop, setCfop] = useState('');
  const [situacaoNota, setSituacaoNota] = useState('todas'); // ativas | canceladas | todas
  const [aba, setAba] = useState<Aba>('nota');
  const [pagina, setPagina] = useState(0);
  const [drawerCodigo, setDrawerCodigo] = useState<string | null>(null);
  const PAGE = 100;

  // Serviço é ISS (municipal): não tem CFOP (conceito do ICMS) nem UF como eixo fiscal.
  // Esconde "Por CFOP" e "Por UF" no serviço; se a aba ativa for uma delas, volta p/ Nota.
  const abasVisiveis = useMemo(
    () => (tipo === 'servico' ? ABAS.filter(a => a.value !== 'cfop' && a.value !== 'uf') : ABAS),
    [tipo],
  );
  useEffect(() => {
    if (tipo === 'servico' && (aba === 'cfop' || aba === 'uf')) setAba('nota');
  }, [tipo, aba]);

  const { data, isFetching } = useQuery<IImpostosPorNota>({
    queryKey: [
      'ai-impostos-por-nota',
      {
        id: empresa?.id,
        tipo,
        modelo: modelo || undefined,
        imposto: imposto || undefined,
        cfop: cfop || undefined,
        ...periodo,
      },
    ],
    queryFn: getImpostosPorNota,
    enabled: !!empresa,
  });

  const fonteOk = !!data?.fonte_disponivel;

  // Listagem nota-a-nota: alimenta a aba "Por Nota" e as opções do filtro de modelo
  // (valores reais), por isso busca sempre que a fonte existe.
  // Volta pra 1ª página quando o filtro/tipo muda.
  useEffect(() => {
    setPagina(0);
  }, [tipo, modelo, imposto, cfop, situacaoNota, empresa?.id]);

  const { data: notasPage, isFetching: loadingNotas } = useQuery<IImpNotasPage>({
    queryKey: [
      'ai-impostos-por-nota-notas',
      {
        id: empresa?.id,
        tipo,
        modelo: modelo || undefined,
        imposto: imposto || undefined,
        cfop: cfop || undefined,
        situacao: situacaoNota,
        limit: PAGE,
        offset: pagina * PAGE,
        ...periodo,
      },
    ],
    queryFn: getImpostosPorNotaNotas,
    enabled: !!empresa && fonteOk,
    placeholderData: keepPreviousData,
  });
  const notas = notasPage?.itens ?? [];
  const totalNotas = notasPage?.total ?? 0;
  const totalPaginas = Math.max(1, Math.ceil(totalNotas / PAGE));

  // ICMS débito (saídas) × crédito (entradas) por mês. tipo ∈ {'DEBITO','CREDITO'}.
  const icmsChart = useMemo(() => {
    const byMes: Record<string, { mes: string; debito: number; credito: number }> = {};
    (data?.icms_deb_cred ?? []).forEach(r => {
      const m = byMes[r.mes] ?? { mes: r.mes, debito: 0, credito: 0 };
      if (r.tipo === 'DEBITO') m.debito += num(r.valor);
      else if (r.tipo === 'CREDITO') m.credito += num(r.valor);
      byMes[r.mes] = m;
    });
    return Object.values(byMes)
      .sort((a, b) => a.mes.localeCompare(b.mes))
      .map(m => ({
        mes: mesLabel(m.mes),
        debito: m.debito,
        credito: m.credito,
        saldo: m.debito - m.credito,
      }));
  }, [data]);

  // Evolução mensal: pivota por mês, uma série por sigla de imposto (valor total).
  const evolucao = useMemo(() => {
    const impostos = Array.from(new Set((data?.evolucao ?? []).map(e => e.imposto)));
    const byMes: Record<string, Record<string, number | string>> = {};
    (data?.evolucao ?? []).forEach(e => {
      const m = byMes[e.mes] ?? { mes: e.mes };
      m[e.imposto] = (Number(m[e.imposto] ?? 0) || 0) + num(e.total_imposto);
      byMes[e.mes] = m;
    });
    const rows = Object.values(byMes)
      .sort((a, b) => String(a.mes).localeCompare(String(b.mes)))
      .map(r => ({ ...r, mes: mesLabel(String(r.mes)) }));
    return { rows, impostos };
  }, [data]);

  // Opções dos filtros — siglas vêm do resumo por imposto; modelos vêm dos valores
  // reais da listagem (sem hardcode). O valor selecionado é unido para nunca sumir.
  const impostoOptions = useMemo(
    () =>
      Array.from(
        new Set([
          ...(data?.por_imposto ?? []).map(r => r.sigla_imposto),
          ...(imposto ? [imposto] : []),
        ]),
      ).sort(),
    [data, imposto],
  );
  const modeloOptions = useMemo(
    () =>
      Array.from(
        new Set([...(notas ?? []).map(n => n.modelo).filter(Boolean), ...(modelo ? [modelo] : [])]),
      ).sort(),
    [notas, modelo],
  );
  const cfopOptions = useMemo(
    () =>
      Array.from(
        // `por_cfop.cfop` vem como numero do backend; normaliza p/ string senao o
        // Set nao deduplica contra o `cfop` selecionado (string) -> dropdown duplicado.
        new Set([
          ...(data?.por_cfop ?? [])
            .map(r => r.cfop)
            .filter(v => v != null && v !== '')
            .map(String),
          ...(cfop ? [cfop] : []),
        ]),
      ).sort(),
    [data, cfop],
  );

  if (!empresa) {
    return (
      <EmptyState
        title="Selecione uma empresa"
        message="Use a busca no topo (ID, CNPJ ou Razão Social) para ver os impostos por nota."
        icon={Hash}
      />
    );
  }

  const filtro = (
    <Flex gap={3} align="flex-end" wrap="wrap" mb={4}>
      <Box>
        <Text fontSize="10.5px" fontWeight={700} color={UI.muted} mb={1} textTransform="uppercase">
          Tipo
        </Text>
        <Select
          size="sm"
          w="140px"
          value={tipo}
          onChange={e => {
            setTipo(e.target.value as NotaTipo);
            setDrawerCodigo(null);
          }}
        >
          {TIPOS.map(t => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
      </Box>
      <Box>
        <Text fontSize="10.5px" fontWeight={700} color={UI.muted} mb={1} textTransform="uppercase">
          Imposto
        </Text>
        <Select
          size="sm"
          w="150px"
          value={imposto}
          onChange={e => {
            setImposto(e.target.value);
            setDrawerCodigo(null);
          }}
        >
          <option value="">Todos</option>
          {impostoOptions.map(s => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
      </Box>
      <Box>
        <Text fontSize="10.5px" fontWeight={700} color={UI.muted} mb={1} textTransform="uppercase">
          Modelo
        </Text>
        <Select
          size="sm"
          w="130px"
          value={modelo}
          isDisabled={modeloOptions.length === 0}
          onChange={e => {
            setModelo(e.target.value);
            setDrawerCodigo(null);
          }}
        >
          <option value="">Todos</option>
          {modeloOptions.map(m => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </Select>
      </Box>
      <Box>
        <Text fontSize="10.5px" fontWeight={700} color={UI.muted} mb={1} textTransform="uppercase">
          CFOP
        </Text>
        <Select
          size="sm"
          w="120px"
          value={cfop}
          isDisabled={cfopOptions.length === 0}
          onChange={e => {
            setCfop(e.target.value);
            setDrawerCodigo(null);
          }}
        >
          <option value="">Todos</option>
          {cfopOptions.map(c => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </Box>
      <Box>
        <Text fontSize="10.5px" fontWeight={700} color={UI.muted} mb={1} textTransform="uppercase">
          Situação
        </Text>
        <Select
          size="sm"
          w="130px"
          value={situacaoNota}
          onChange={e => {
            setSituacaoNota(e.target.value);
            setDrawerCodigo(null);
          }}
        >
          <option value="todas">Todas</option>
          <option value="ativas">Ativas</option>
          <option value="canceladas">Canceladas</option>
        </Select>
      </Box>
    </Flex>
  );

  if (isFetching && !data) {
    return (
      <Box>
        {filtro}
        <Box textAlign="center" py={16}>
          <Spinner />
        </Box>
      </Box>
    );
  }

  if (data && !data.fonte_disponivel) {
    return (
      <Box>
        {filtro}
        <EmptyState
          title="Fonte não disponível"
          message="As tabelas de impostos por nota ainda não foram migradas neste ambiente. Assim que estiverem, os dados aparecem aqui."
          icon={Hash}
        />
      </Box>
    );
  }

  const k = data?.kpis;
  // A fonte (extract_impostos_nota_*) vem inflada da extração (fan-out ~1314x) em
  // ~43% das notas → carga média impossível (>100%). Avisamos e apontamos a Apuração.
  const cargaInconsistente = num(k?.carga_media) > 100;

  return (
    <Box>
      {filtro}

      {cargaInconsistente && (
        <Flex
          align="flex-start"
          gap={2.5}
          p={3.5}
          mb={4}
          borderRadius="14px"
          bg={UI.negSoft}
          border={`1px solid ${UI.neg}33`}
        >
          <Box color={UI.neg} mt="1px" flexShrink={0}>
            <WarningCircle size={18} weight="fill" />
          </Box>
          <Box flex={1}>
            <Text fontSize="13px" fontWeight={800} color={UI.neg}>
              Imposto por nota inconsistente na origem
            </Text>
            <Text fontSize="12px" color={UI.ink}>
              A base de cálculo desta fonte está inflada na extração (carga média de{' '}
              {pct(k?.carga_media)} é impossível) — esta tela fica confiável após a correção do ETL.
            </Text>
          </Box>
          <Button
            size="sm"
            flexShrink={0}
            bg={UI.neg}
            color="white"
            _hover={{ bg: '#C53B30' }}
            rightIcon={<ArrowSquareOut size={14} weight="bold" />}
            onClick={() => onScreen('impostos-apurados')}
          >
            Ver Apurados
          </Button>
        </Flex>
      )}

      <Grid templateColumns={{ base: '1fr 1fr', md: 'repeat(4, 1fr)' }} gap={4} mb={5}>
        <KpiCard
          label="Notas com imposto"
          value={qtd(k?.total_notas)}
          sub={`de ${qtd(k?.total_notas_tipo)} notas do tipo no período`}
          icon={FileText}
          iconColor={UI.brand}
          iconBg={UI.infoSoft}
          info={
            <InfoBloco
              oque="Notas do tipo selecionado COM imposto destacado, de quantas notas existem no período. Nota sem imposto lançado não aparece nesta tela (mas aparece em Notas Fiscais) — por isso o número aqui costuma ser menor que o total de notas."
              dados="extract_impostos_nota_{saida,entrada,servico} (com imposto, COUNT distinto) ÷ extract_notas_{saida,entrada,servico} (total do tipo no período)"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Base total"
          value={formatCurrency(k?.total_base ?? 0)}
          icon={CurrencyDollar}
          iconColor={UI.pos}
          iconBg={UI.posSoft}
          info={
            <InfoBloco
              oque="Soma do valor das notas do período — a base sobre a qual a carga tributária é calculada."
              dados="extract_impostos_nota_*.valor_nota (somado por nota distinta)"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Impostos"
          value={formatCurrency(k?.total_impostos ?? 0)}
          icon={Hash}
          iconColor={UI.warn}
          iconBg={UI.warnSoft}
          info={
            <InfoBloco
              oque="Total de impostos lançados nas notas do período (soma de todas as siglas de imposto: ICMS, PIS, COFINS, IPI, ISS...)."
              dados="extract_impostos_nota_*.valor_imposto (SUM)"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Carga média"
          value={pct(k?.carga_media, 2)}
          icon={Percent}
          iconColor="#8B5CF6"
          iconBg="#F0EAFB"
          info={
            <InfoBloco
              oque="Percentual médio de imposto sobre o valor das notas (total de impostos ÷ base total). Acima de 100% indica fonte inflada na extração."
              dados="SUM(valor_imposto) ÷ SUM(valor_nota) × 100 em extract_impostos_nota_*"
              filtro={FILTRO.empresa}
            />
          }
        />
      </Grid>

      {/* Abas */}
      <Box
        bg="white"
        border="1px solid"
        borderColor={UI.border}
        borderRadius="16px"
        overflow="hidden"
        mb={5}
      >
        <Flex gap={1} borderBottom="1px solid #E7ECF2" px={2} wrap="wrap">
          {abasVisiveis.map(t => {
            const isActive = t.value === aba;
            return (
              <Flex
                key={t.value}
                px={4}
                py={3}
                cursor="pointer"
                onClick={() => setAba(t.value)}
                borderBottom="2.5px solid"
                borderColor={isActive ? UI.brand : 'transparent'}
                color={isActive ? UI.brand : UI.muted}
                mb="-1px"
                _hover={{ color: isActive ? UI.brand : UI.ink }}
              >
                <Text fontSize="13px" fontWeight={700}>
                  {t.label}
                </Text>
              </Flex>
            );
          })}
        </Flex>

        <TableContainer>
          {/* Por Nota — listagem nota-a-nota; clique abre o drawer */}
          {aba === 'nota' && (
            <Table variant="simple" size="sm">
              <Thead bg={UI.rowHover}>
                <Tr>
                  <Th>NF / Série</Th>
                  <Th>Data</Th>
                  <Th>CFOP</Th>
                  <Th>Parceiro</Th>
                  <Th isNumeric>Valor</Th>
                  <Th isNumeric>Impostos</Th>
                  <Th isNumeric>% Carga</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loadingNotas && (
                  <Tr>
                    <Td colSpan={7} textAlign="center" py={8}>
                      <Spinner />
                    </Td>
                  </Tr>
                )}
                {!loadingNotas &&
                  (notas ?? []).map(n => (
                    <Tr
                      key={n.codigo_nota}
                      cursor="pointer"
                      _hover={{ bg: UI.rowHover }}
                      onClick={() => setDrawerCodigo(n.codigo_nota)}
                      title="Ver detalhes da nota"
                    >
                      <Td fontWeight={700}>
                        {n.numero_nota}
                        {n.serie ? ` / ${n.serie}` : ''}
                        {n.situacao != null && !['N', '1', 'A'].includes(n.situacao) && (
                          <Box
                            as="span"
                            ml={2}
                            px={1.5}
                            borderRadius="full"
                            fontSize="9.5px"
                            fontWeight={700}
                            style={{ background: `${UI.neg}1A`, color: UI.neg }}
                          >
                            Cancelada
                          </Box>
                        )}
                      </Td>
                      <Td>{n.data_nota}</Td>
                      <Td
                        maxW="200px"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        <CfopCelula cfop={n.cfop} natureza={n.natureza} />
                      </Td>
                      <Td
                        maxW="240px"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        <Text fontWeight={600} fontSize="12.5px">
                          {n.parceiro}
                        </Text>
                        <Text fontSize="11px" color={UI.muted}>
                          {ufCnpj(n.uf, n.cnpj)}
                        </Text>
                      </Td>
                      <Td isNumeric>{formatCurrency(n.valor_nota)}</Td>
                      <Td isNumeric fontWeight={700}>
                        {n.tem_imposto ? (
                          formatCurrency(n.total_impostos)
                        ) : (
                          <Text as="span" color={UI.faint} fontWeight={600} fontSize="11.5px">
                            sem imposto
                          </Text>
                        )}
                      </Td>
                      <Td isNumeric color={num(n.pct_carga) > 18 ? UI.neg : UI.ink}>
                        {n.tem_imposto ? pct(n.pct_carga) : '—'}
                      </Td>
                    </Tr>
                  ))}
                {!loadingNotas && (notas ?? []).length === 0 && <Empty colSpan={7} />}
              </Tbody>
            </Table>
          )}
          {aba === 'nota' && totalPaginas > 1 && (
            <Flex
              justify="space-between"
              align="center"
              mt={3}
              px={1}
              fontSize="12.5px"
              color={UI.muted}
            >
              <Text>
                {totalNotas.toLocaleString('pt-BR')} nota(s) · página <b>{pagina + 1}</b> de{' '}
                <b>{totalPaginas}</b>
              </Text>
              <Flex gap={2}>
                <Button
                  size="xs"
                  variant="ghost"
                  isDisabled={pagina === 0 || loadingNotas}
                  onClick={() => setPagina(p => Math.max(0, p - 1))}
                >
                  Anterior
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  isDisabled={pagina + 1 >= totalPaginas || loadingNotas}
                  onClick={() => setPagina(p => p + 1)}
                >
                  Próxima
                </Button>
              </Flex>
            </Flex>
          )}

          {/* Por Imposto */}
          {aba === 'imposto' && (
            <Table variant="simple" size="sm">
              <Thead bg={UI.rowHover}>
                <Tr>
                  <Th>Imposto</Th>
                  <Th isNumeric>Registros</Th>
                  <Th isNumeric>Base</Th>
                  <Th isNumeric>Alíq. média</Th>
                  <Th isNumeric>Valor</Th>
                  <Th isNumeric>Isentas</Th>
                  <Th isNumeric>Outras</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(data?.por_imposto ?? []).map(r => (
                  <Tr key={r.sigla_imposto} _hover={{ bg: UI.rowHover }}>
                    <Td fontWeight={700}>
                      <ChakraTooltip
                        label={impostoDesc(r.sigla_imposto)}
                        hasArrow
                        placement="top"
                        openDelay={150}
                        fontSize="12px"
                        maxW="290px"
                        borderRadius="8px"
                        px={3}
                        py={2}
                      >
                        <Text
                          as="span"
                          borderBottom="1px dashed"
                          borderColor={UI.faint}
                          cursor="help"
                        >
                          {r.sigla_imposto}
                        </Text>
                      </ChakraTooltip>
                    </Td>
                    <Td isNumeric>{qtd(r.qtd_registros)}</Td>
                    <Td isNumeric>{formatCurrency(r.total_base)}</Td>
                    <Td isNumeric>{pct(r.aliquota_media, 2)}</Td>
                    <Td isNumeric fontWeight={700}>
                      {formatCurrency(r.total_imposto)}
                    </Td>
                    <Td isNumeric color={UI.muted}>
                      {formatCurrency(r.total_isentas)}
                    </Td>
                    <Td isNumeric color={UI.muted}>
                      {formatCurrency(r.total_outras)}
                    </Td>
                  </Tr>
                ))}
                {(data?.por_imposto ?? []).length === 0 && <Empty colSpan={7} />}
              </Tbody>
            </Table>
          )}

          {/* Por CFOP */}
          {aba === 'cfop' && (
            <Table variant="simple" size="sm">
              <Thead bg={UI.rowHover}>
                <Tr>
                  <Th>CFOP</Th>
                  <Th>Natureza</Th>
                  <Th isNumeric>Notas</Th>
                  <Th isNumeric>ICMS</Th>
                  <Th isNumeric>PIS</Th>
                  <Th isNumeric>COFINS</Th>
                  <Th isNumeric>Total impostos</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(data?.por_cfop ?? []).map(r => (
                  <Tr key={r.cfop} _hover={{ bg: UI.rowHover }}>
                    <Td fontWeight={700}>{r.cfop}</Td>
                    <Td maxW="240px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                      {r.natureza}
                    </Td>
                    <Td isNumeric>{qtd(r.qtd_notas)}</Td>
                    <Td isNumeric>{formatCurrency(r.icms)}</Td>
                    <Td isNumeric>{formatCurrency(r.pis)}</Td>
                    <Td isNumeric>{formatCurrency(r.cofins)}</Td>
                    <Td isNumeric fontWeight={700}>
                      {formatCurrency(r.total_impostos)}
                    </Td>
                  </Tr>
                ))}
                {(data?.por_cfop ?? []).length === 0 && <Empty colSpan={7} />}
              </Tbody>
            </Table>
          )}

          {/* Por UF */}
          {aba === 'uf' && (
            <Table variant="simple" size="sm">
              <Thead bg={UI.rowHover}>
                <Tr>
                  <Th>UF</Th>
                  <Th isNumeric>Notas</Th>
                  <Th isNumeric>Faturamento</Th>
                  <Th isNumeric>ICMS</Th>
                  <Th isNumeric>Alíq. média ICMS</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(data?.por_uf ?? []).map(r => (
                  <Tr key={r.uf || 'sem-uf'} _hover={{ bg: UI.rowHover }}>
                    <Td fontWeight={700}>{r.uf || 'Sem UF'}</Td>
                    <Td isNumeric>{qtd(r.qtd_notas)}</Td>
                    <Td isNumeric>{formatCurrency(r.faturamento)}</Td>
                    <Td isNumeric fontWeight={700}>
                      {formatCurrency(r.icms)}
                    </Td>
                    <Td isNumeric>{pct(r.aliq_media_icms, 2)}</Td>
                  </Tr>
                ))}
                {(data?.por_uf ?? []).length === 0 && <Empty colSpan={5} />}
              </Tbody>
            </Table>
          )}

          {/* Por Cliente */}
          {aba === 'cliente' && (
            <Table variant="simple" size="sm">
              <Thead bg={UI.rowHover}>
                <Tr>
                  <Th>Parceiro</Th>
                  <Th>UF</Th>
                  <Th isNumeric>Notas</Th>
                  <Th isNumeric>Faturamento</Th>
                  <Th isNumeric>Impostos</Th>
                  <Th isNumeric>% Carga</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(data?.por_cliente ?? []).map(r => (
                  <Tr
                    key={`${r.cnpj}-${r.parceiro}`}
                    cursor="pointer"
                    _hover={{ bg: UI.rowHover }}
                    onClick={() => onOpenNotas(r.cnpj || r.parceiro, tipo)}
                    title={`Ver as notas de ${r.parceiro}`}
                  >
                    <Td maxW="240px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                      <Text fontWeight={700} fontSize="12.5px">
                        {r.parceiro}
                      </Text>
                      <Text fontSize="11px" color={UI.muted}>
                        {r.cnpj || '—'}
                      </Text>
                    </Td>
                    <Td>{r.uf || '—'}</Td>
                    <Td isNumeric>{qtd(r.qtd_notas)}</Td>
                    <Td isNumeric>{formatCurrency(r.faturamento)}</Td>
                    <Td isNumeric fontWeight={700}>
                      {formatCurrency(r.total_impostos)}
                    </Td>
                    <Td isNumeric>{pct(r.pct_carga)}</Td>
                  </Tr>
                ))}
                {(data?.por_cliente ?? []).length === 0 && <Empty colSpan={6} />}
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </Box>

      {/* Gráficos */}
      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={5}>
        <Painel title="ICMS — débito (saídas) × crédito (entradas)">
          {icmsChart.length === 0 ? (
            <Text fontSize="13px" color={UI.muted}>
              Sem dados de ICMS no período (requer notas de saída e entrada).
            </Text>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={icmsChart}>
                <CartesianGrid strokeDasharray="3 3" stroke={UI.border} />
                <XAxis dataKey="mes" fontSize={11} stroke={UI.muted} />
                <YAxis
                  fontSize={11}
                  stroke={UI.muted}
                  width={70}
                  tickFormatter={v => formatCurrency(v)}
                />
                <Tooltip formatter={(v: number) => formatCurrency(v)} />
                <Legend />
                <Bar dataKey="debito" name="Débito" fill={UI.neg} radius={[4, 4, 0, 0]} />
                <Bar dataKey="credito" name="Crédito" fill={UI.pos} radius={[4, 4, 0, 0]} />
                <Bar dataKey="saldo" name="Saldo" fill={UI.brand} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Painel>

        <Painel title="Evolução mensal — tributos por mês">
          {evolucao.rows.length === 0 ? (
            <Text fontSize="13px" color={UI.muted}>
              Sem evolução no período.
            </Text>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={evolucao.rows}>
                <CartesianGrid strokeDasharray="3 3" stroke={UI.border} />
                <XAxis dataKey="mes" fontSize={11} stroke={UI.muted} />
                <YAxis
                  fontSize={11}
                  stroke={UI.muted}
                  width={70}
                  tickFormatter={v => formatCurrency(v)}
                />
                <Tooltip formatter={(v: number) => formatCurrency(v)} />
                <Legend />
                {evolucao.impostos.map((imp, i) => (
                  <Line
                    key={imp}
                    type="monotone"
                    dataKey={imp}
                    name={imp}
                    stroke={SERIE_COLORS[i % SERIE_COLORS.length]}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </Painel>
      </Grid>

      {drawerCodigo && (
        <NoteDrawer
          empresaId={empresa.id}
          tipo={tipo}
          codigo={drawerCodigo}
          isOpen={!!drawerCodigo}
          onClose={() => setDrawerCodigo(null)}
        />
      )}
    </Box>
  );
}
